import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../../../ui/YhInput";
import YhButton from "../../../../../../../../ui/YhButton";
import customErrorHandler from "../../../../../../../../utils/customErrorHandler";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../../../helpers/methods";
import notifiers from "../../../../../../../../global/constants/NotificationConstants";
import {
  addDesingation,
  deleteDesignation,
  getAllDesignation,
} from "./CreateDesginationService";

interface CustomProps {
  backStep: Function;
  handleCloseCreateTeam: Function;
  nextStep?: () => void | undefined;
  selectedDept: any;
  hideFooterButtons?: boolean;
}

const CreateDesignation = (props: CustomProps) => {
  const [designation, setDesignation] = useState({
    designation_name: "",
    _id: "",
  });
  const [designationList, setDesignationList] = useState<any[]>([]);
  const [inputError, setInputError] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState("");

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedDept?._id]);

  const init = async () => {
    if (!props?.selectedDept?._id) return;
    try {
      const response = await getAllDesignation(props.selectedDept._id);
      customErrorHandler(response);
      setDesignationList(response.data || []);
    } catch (error: any) {
      if (!props.selectedDept.is_child && error.message === "No Data found") {
        setDesignationList([]);
      } else {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  const handleAddDesignation = async () => {
    if (!designation.designation_name.trim()) {
      setInputError(true);
      return;
    }
    try {
      setSaving(true);
      const newDesignation: any = {
        designation_name: designation.designation_name,
        _id: "",
      };
      const response = await addDesingation(
        newDesignation,
        props.selectedDept._id
      );
      customErrorHandler(response);
      openSuccessNotification(response.message);
      setDesignationList((list) => [
        ...list,
        {
          designation_name: response.data.designation_name,
          _id: response.data._id,
        },
      ]);
      setDesignation({ designation_name: "", _id: "" });
      setInputError(false);
      setShowAddForm(false);
      setExpanded(true);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (designation_id: string) => {
    try {
      const response = await deleteDesignation(designation_id);
      customErrorHandler(response);
      openSuccessNotification(response.message);
      setDesignationList((list) =>
        list.filter((obj) => obj._id !== designation_id)
      );
      setConfirmId("");
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const deptName = props.selectedDept?.department_name || "Department";

  return (
    <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
      {/* Left — selected dept + add form */}
      <div className="flex min-h-0 flex-col rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.03)]">
        <div className="mb-5 flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FF] text-[#A02448]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01" />
            </svg>
          </span>
          <div>
            <h2 className="text-lg font-bold text-[#111827]">
              Create Designation
            </h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              Add designations under the selected department
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <YhInput
            label="Selected Department"
            value={deptName}
            disabled
            readOnly
          />

          <YhInput
            label="Designation Name"
            requiredMark
            placeholder="e.g. Sr. Executive HR"
            value={designation.designation_name}
            error={inputError ? "Designation name is required" : ""}
            onChange={(e) => {
              setInputError(false);
              setDesignation({ designation_name: e.target.value, _id: "" });
            }}
          />

          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={handleAddDesignation}
          >
            + Add Designation
          </YhButton>
        </div>
      </div>

      {/* Right — designations list like screenshot */}
      <div className="flex min-h-0 flex-col rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.03)]">
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FF] text-[#A02448]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="9" y="2" width="6" height="5" rx="1" />
              <rect x="2" y="17" width="6" height="5" rx="1" />
              <rect x="16" y="17" width="6" height="5" rx="1" />
              <path d="M12 7v4M5 13v4M19 13v4M5 13h14" />
            </svg>
          </span>
          <div>
            <h2 className="text-lg font-bold text-[#111827]">
              Departments Designations
            </h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              Add and manage designations for this department.
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto [scrollbar-width:thin]">
          <div className="rounded-xl border border-[#EEF0F5]">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-3 text-left"
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-[#22C55E]" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#111827]">
                {deptName}
              </span>
              <span className="shrink-0 rounded-full bg-[#EEF0FF] px-2.5 py-0.5 text-xs font-semibold text-[#A02448]">
                {designationList.length} Designation
                {designationList.length === 1 ? "" : "s"}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
                className={`shrink-0 transition ${expanded ? "rotate-180" : ""}`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {expanded ? (
              <ul className="border-t border-[#F3F4F6] px-2 pb-2">
                {designationList.length === 0 ? (
                  <li className="px-2 py-4 text-center text-sm text-[#9CA3AF]">
                    No designations yet
                  </li>
                ) : (
                  designationList.map((item: any) => (
                    <li
                      key={item._id}
                      className="mt-2 flex items-center gap-3 rounded-xl border border-[#EEF0F5] bg-[#FAFBFC] px-3 py-2.5"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-[#A02448]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#111827]">
                        {item.designation_name}
                      </span>
                      {confirmId === item._id ? (
                        <div className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            className="text-xs font-semibold text-[#DC2626]"
                            onClick={() => handleRemove(item._id)}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            className="text-xs font-semibold text-[#6B7280]"
                            onClick={() => setConfirmId("")}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#DC2626] hover:underline"
                          onClick={() => setConfirmId(item._id)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                          </svg>
                          Remove
                        </button>
                      )}
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>

          {showAddForm ? (
            <div className="mt-3 rounded-xl border border-[#F9F0F4] bg-[#F8F9FF] p-3">
              <YhInput
                label="New Designation"
                placeholder="e.g. Sr. Executive HR"
                value={designation.designation_name}
                error={inputError ? "Required" : ""}
                onChange={(e) => {
                  setInputError(false);
                  setDesignation({ designation_name: e.target.value, _id: "" });
                }}
              />
              <div className="mt-2 flex gap-2">
                <YhButton
                  variant="primary"
                  size="sm"
                  loading={saving}
                  onClick={handleAddDesignation}
                >
                  Save
                </YhButton>
                <YhButton
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false);
                    setDesignation({ designation_name: "", _id: "" });
                    setInputError(false);
                  }}
                >
                  Cancel
                </YhButton>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4B8C0] bg-[#F5F3FF] text-sm font-semibold text-[#A02448] transition hover:bg-[#EEF0FF]"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add Designation
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDesignation;
